
declare global {
  interface Window {
    gtag?: (...args: any[]) => void
  }
}

export type AnalyticsVisitorStatus =
  | 'authenticated'
  | 'guest'

type PageViewParams = {
  path: string
  pageType: string
  visitorStatus: AnalyticsVisitorStatus
  title?: string
  navigationKey: string
}

const isProduction =
  process.env.REACT_APP_ENV === 'production'

// Capture the original browser entry before SPA navigation.
const initialReferrer =
  typeof document !== 'undefined'
    ? document.referrer
    : ''

const initialQuery =
  typeof window !== 'undefined'
    ? window.location.search
    : ''

const allowedCampaignParams = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_id',
  'utm_content',
  'utm_term'
]

const getInitialCampaignQuery = (): string => {
  const original = new URLSearchParams(initialQuery)
  const safe = new URLSearchParams()

  for (const key of allowedCampaignParams) {
    const value = original.get(key)

    if (
      value &&
      value.length <= 100 &&
      /^[a-zA-Z0-9_. -]+$/.test(value)
    ) {
      safe.set(key, value)
    }
  }

  return safe.toString()
}

const initialCampaignQuery = getInitialCampaignQuery()

let previousPageUrl: string | null = null
const trackedNavigations = new Set<string>()

export const trackPageView = ({
  path,
  pageType,
  visitorStatus,
  title = 'AllFans',
  navigationKey
}: PageViewParams): boolean => {
  if (
    !isProduction ||
    !window?.gtag
  ) {
    return false
  }

  if (trackedNavigations.has(navigationKey)) {
    return false
  }

  const url = new URL(path, window.location.origin)

  // Never accept query strings from page components.
  url.search = ''
  url.hash = ''

  const firstPage = previousPageUrl === null

  if (firstPage && initialCampaignQuery) {
    url.search = initialCampaignQuery
  }

  const parameters: Record<string, string> = {
    page_location: url.toString(),
    page_title: title,
    page_type: pageType,
    visitor_status: visitorStatus,
    content_group: pageType
  }

  if (previousPageUrl) {
    parameters.page_referrer = previousPageUrl
  } else if (initialReferrer) {
    parameters.page_referrer = initialReferrer
  }

  window.gtag('set', {
    page_location: url.toString(),
    page_title: title
  })

  window.gtag('event', 'page_view', parameters)

  trackedNavigations.add(navigationKey)

  previousPageUrl = `${url.origin}${url.pathname}`

  return true
}
